import React, {useMemo, useState} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {
    Box,
    Flex,
    List,
    ListItem,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Select
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {EnterKeyNumber} from '../../../constants'
import StyledContent from '../../../components/styled-content'

export const FAQAccordion = ({data}) => {
    const location = useLocation()
    const history = useHistory()
    const [accordionIndex, setAccordionIndex] = useState(0)

    const selectedCategoryIdx = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        const categoryId = searchParams.get('category')
        return categoryId ? data.findIndex((item) => item.id === categoryId) : 0
    }, [location.search, data])

    const handleCategoryClick = (category) => {
        if (category.id !== data[selectedCategoryIdx].id) {
            const newParams = new URLSearchParams(location.search)
            newParams.set('category', category.id)
            history.push({search: newParams.toString()})
            setAccordionIndex(0)
            window.scrollTo(0, 0)
        }
    }

    const filteredItems = useMemo(() => {
        return data[selectedCategoryIdx]?.items?.filter((item) => item?.name?.trim()) || []
    }, [data, selectedCategoryIdx])

    return (
        <Flex
            gap={8}
            direction={{base: 'column', md: 'row'}}
            backgroundColor="stokkeCore.white"
            padding={8}
            marginX={-8}
            width="100%"
        >
            <Box maxW="343px" w="100%">
                <Text textStyle="Regular 2XLarge" pb={2}>
                    <FormattedMessage defaultMessage="FAQs" id="customer_service.faq.faqs" />
                </Text>
                <Select
                    onChange={(e) => {
                        handleCategoryClick(data[Number(e.target.value)])
                    }}
                    value={selectedCategoryIdx}
                    display={{base: 'block', md: 'none'}}
                    data-testid="accordion-select"
                >
                    {data.map((category, idx) => (
                        <option key={idx} value={idx}>
                            {category.name.length > 31
                                ? `${category.name.slice(0, 31)}…`
                                : category.name}
                        </option>
                    ))}
                </Select>
                <List
                    spacing={3}
                    display={{base: 'none', md: 'block'}}
                    data-testid="accordion-list"
                >
                    {data?.map((category) => (
                        <ListItem
                            tabIndex="0"
                            onKeyDown={(e) => {
                                if (e.keyCode === EnterKeyNumber) {
                                    handleCategoryClick(category)
                                }
                            }}
                            key={category.id}
                            cursor="pointer"
                            onClick={() => handleCategoryClick(category)}
                            bg={
                                data[selectedCategoryIdx]?.id === category?.id
                                    ? 'stokkeGray.mid'
                                    : 'transparent'
                            }
                            p={2}
                            _hover={{bg: 'stokkeGray.mid'}}
                        >
                            {category?.name}
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Box flex="1">
                <Text mb={4} textStyle="Regular 2XLarge" data-testid="category-title">
                    {data[selectedCategoryIdx]?.name}
                </Text>
                <Accordion
                    allowToggle
                    key={data[selectedCategoryIdx]?.id}
                    index={accordionIndex}
                    onChange={setAccordionIndex}
                >
                    {filteredItems.map((item) => (
                        <AccordionItem key={item?.id} value={item?.id}>
                            <div>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left" textStyle="Regular XLarge">
                                        {item?.name}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </div>
                            <AccordionPanel pb={4} textStyle="Regular Medium">
                                <StyledContent content={item?.description} />
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Box>
        </Flex>
    )
}

FAQAccordion.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                    description: PropTypes.string
                })
            )
        })
    ).isRequired
}
